import { Plus, X } from '@/components/ui/icons';
import { useRef, useState } from 'react';
import { canAssignRole, canEditMemberRoles, canGrantPermissions, type RoleDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { serverApi } from '@/api/endpoints';
import { Menu } from '@/components/ui/Popover';
import { useMemberRank, useMyPermissions } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { useMemberRoles, useServerRoles, useServers } from '@/stores/servers';
import { toast } from '@/stores/ui';

const dot = (role: RoleDTO) => <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: role.color ?? 'var(--clanfire-fg-muted)' }} />;

/**
 * A member's server roles as chips. With Manage Roles, × takes a role away and +
 * gives one: for yourself, or members below you, and only roles below your own
 * highest role whose permissions you have (the owner can use any).
 */
export function MemberRoles({
  serverId,
  userId,
  label = 'Roles',
  heading = false,
  className,
}: {
  serverId: string;
  userId: string;
  /** The list's accessible name, and its visible heading with `heading`. */
  label?: string;
  heading?: boolean;
  className?: string;
}) {
  const myId = useAuth((s) => s.user?.id ?? '');
  const roles = useMemberRoles(serverId, userId);
  const serverRoles = useServerRoles(serverId);
  const perms = useMyPermissions(serverId);
  const target = useMemberRank(serverId, userId);
  const addRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const editable = perms.can('MANAGE_ROLES') && canEditMemberRoles({ userId: myId, rank: perms.rank }, { userId, rank: target.rank, isOwner: target.isOwner });
  const canRemove = (role: RoleDTO) => editable && canAssignRole(perms, role);
  const addable = editable
    ? serverRoles.filter((r) => !roles.some((held) => held.id === r.id) && canAssignRole(perms, r) && canGrantPermissions(perms.permissions, r.permissions))
    : [];
  if (!roles.length && !addable.length) return null;

  const change = async (role: RoleDTO, give: boolean) => {
    setBusy(true);
    try {
      const member = await (give ? serverApi.addMemberRole : serverApi.removeMemberRole)(serverId, userId, role.id);
      const servers = useServers.getState();
      servers.upsertMember(member);
      const server = servers.servers[serverId];
      if (server && userId === myId) servers.patchServer(serverId, { me: { ...server.me, roleIds: member.roleIds } });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const chips = (
    <ul className={cn('flex flex-wrap items-center gap-1.5', className)} aria-label={label}>
      {roles.map((role) => (
        <li key={role.id} className="inline-flex items-center gap-1.5 rounded-md bg-hover py-1 pr-2 pl-2 text-xs font-semibold text-fg">
          {dot(role)}
          {role.name}
          {canRemove(role) && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void change(role, false)}
              aria-label={`Remove the ${role.name} role`}
              title={`Remove ${role.name}`}
              className="-mr-1 rounded p-0.5 text-fg-muted hover:bg-danger hover:text-white disabled:opacity-50"
            >
              <X size={12} />
            </button>
          )}
        </li>
      ))}
      {addable.length > 0 && (
        <li>
          <button
            ref={addRef}
            type="button"
            disabled={busy}
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Add a role"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title="Add a role"
            className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-hover text-fg-muted hover:bg-accent hover:text-accent-fg disabled:opacity-50"
          >
            <Plus size={14} />
          </button>
          <Menu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            anchor={addRef}
            label="Add a role"
            items={addable.map((role) => ({ label: role.name, icon: dot(role), onSelect: () => void change(role, true) }))}
          />
        </li>
      )}
    </ul>
  );

  if (!heading) return chips;
  return (
    <section>
      <h3 className="mb-1.5 text-[11px] font-bold tracking-wider text-fg-muted uppercase">{label}</h3>
      {chips}
    </section>
  );
}
